import { createInertiaApp } from '@inertiajs/react'
import { type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import ErrorBoundary from '@/components/ErrorBoundary'

interface PageModule {
  default: ComponentType & { layout?: (page: React.ReactNode) => React.ReactNode }
}

const pages = import.meta.glob<PageModule>(['../pages/**/*.tsx', '!**/*.test.tsx'], { eager: true })

// Register the service worker so the app is installable (PWA). The worker does
// no caching (see app/views/pwa/service-worker.js) — it only enables the install
// prompt. Service workers require a secure context (HTTPS, or localhost in dev).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker').catch(() => {
      // Non-fatal: the app works fine without it; it just isn't installable.
    })
  })
}

// Brand accent for the progress bar, read from the themed CSS var so it
// re-colors per partner (white-label); base.css also styles the bar via the
// same var. Falls back to the Collectia default if the var isn't resolved yet.
const brandAccent =
  getComputedStyle(document.documentElement).getPropertyValue('--color-brand-turquoise').trim() || '#20B894'

void createInertiaApp({
  progress: {
    delay: 150,
    color: brandAccent,
    includeCSS: true,
    showSpinner: false,
  },

  setup({ el, App, props }) {
    createRoot(el!).render(
      <ErrorBoundary>
        <App {...props} />
      </ErrorBoundary>,
    )
  },

  resolve(name) {
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      throw new Error(`Page not found: ${name}`)
    }
    return page
  },

  strictMode: true,

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' }
    },
  },
}).catch((error) => {
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
